import { useEffect, useId } from 'react';
import { Box } from '@mui/material';

import type { GeneratedIntakeField, GeneratedIntakeGroupHelp, IntakeFieldValue } from '../../types';

import { GroupHelpTrigger, HelpPanelDrawer } from './HelpPanelDrawer';
import { IntakePageFieldInput } from './IntakePageFieldInput';
import { IntakePageFooter } from './IntakePageFooter';
import { IntakePageHeader } from './IntakePageHeader';
import { IntakePayerTable } from './IntakePayerTable';
import { cardSx, chipSx, groupTitleSx, wf } from './tokens';
import type { PayerRow } from './types';
import { useGroupHelpPanel } from './useGroupHelpPanel';

interface IntakePayersPageProps {
  acknowledgedValues: Record<string, IntakeFieldValue>;
  disabledReasons?: Record<string, string>;
  draftValues: Record<string, IntakeFieldValue>;
  fields: GeneratedIntakeField[];
  groups?: GeneratedIntakeGroupHelp[];
  isSaving?: boolean;
  nextLabel: string;
  onBack?: () => void;
  onContinue: () => void;
  onDraftChange: (field: GeneratedIntakeField, value: IntakeFieldValue) => void;
  onHelpAction?: (action: 'message_specialist') => void;
  onPersistField: (field: GeneratedIntakeField, value: IntakeFieldValue) => Promise<void>;
  payerRows: PayerRow[];
  onSaveChanges?: () => Promise<void> | void;
  saveDisabled?: boolean;
  saveErrorCount?: number;
  saveMode?: 'autosave' | 'explicit';
  embedded?: boolean;
  showRequiredErrors?: boolean;
  onReviewStateChange?: (state: {
    dirty: boolean;
    error: boolean;
    saving: boolean;
    valid: boolean;
  }) => void;
}

/**
 * M4 remains because it owns generated, non-payer requirements such as Availity.
 * Payer management itself now lives in the canonical table on Key Questions.
 */
export function IntakePayersPage({
  acknowledgedValues,
  disabledReasons = {},
  draftValues,
  fields,
  groups = [],
  isSaving = false,
  nextLabel,
  onBack,
  onContinue,
  onDraftChange,
  onHelpAction = () => undefined,
  onPersistField,
  payerRows,
  onSaveChanges,
  saveDisabled = false,
  saveErrorCount = 0,
  saveMode = 'autosave',
  embedded = false,
  showRequiredErrors = false,
  onReviewStateChange,
}: IntakePayersPageProps) {
  const { selectedGroup, selectedGroupId, openGroup, closeGroup } = useGroupHelpPanel(groups);
  const drawerId = `intake-payers-help-${useId().replace(/:/g, '')}`;
  const availityGroup = groups.find(group => group.group_id === 'I23');
  const availityField = fields.find(field => field.group_id === 'I23');
  const availityValue = availityField ? draftValues[availityField.field_id] : undefined;
  const availityDirty = Boolean(
    availityField && availityValue !== acknowledgedValues[availityField.field_id],
  );
  const availityValid = !availityField?.is_required || availityValue === true;

  useEffect(() => {
    onReviewStateChange?.({
      dirty: availityDirty,
      error: saveErrorCount > 0,
      saving: isSaving,
      valid: availityValid,
    });
  }, [availityDirty, availityValid, isSaving, onReviewStateChange, saveErrorCount]);

  return (
    <>
      {!embedded && (
        <IntakePageHeader
          title="Payers"
          sub="Complete any payer access requirements for your practice."
        />
      )}

      <IntakePayerTable rows={payerRows} readOnlyLabel="managed from Key questions" />

      {availityField ? (
        <Box sx={cardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Box sx={groupTitleSx}>Availity access</Box>
            {availityGroup?.help_panel && (
              <GroupHelpTrigger
                groupName={availityGroup.group_name}
                controlsId={drawerId}
                expanded={selectedGroupId === 'I23'}
                onOpen={opener => openGroup('I23', opener)}
              />
            )}
          </Box>
          <Box sx={{ color: wf.muted, fontSize: '12.5px', mb: 0.5 }}>{availityField.help_text}</Box>
          <IntakePageFieldInput
            field={{ ...availityField, help_text: '' }}
            draftValue={availityValue ?? false}
            acknowledgedValue={acknowledgedValues[availityField.field_id] ?? false}
            onDraftChange={onDraftChange}
            onPersist={onPersistField}
            disabled={Boolean(disabledReasons[availityField.field_id])}
            disabledReason={disabledReasons[availityField.field_id]}
            saveMode={saveMode}
            showStatus={false}
            validationError={
              showRequiredErrors && !availityValid
                ? `${availityField.label} is required.`
                : undefined
            }
          />
          <Box sx={{ ...chipSx('pend'), mt: 0.25 }}>exact steps pending confirmation</Box>
        </Box>
      ) : null}

      {saveErrorCount > 0 && (
        <Box sx={{ color: 'error.main', fontSize: '0.8rem' }}>
          {saveErrorCount} change{saveErrorCount === 1 ? '' : 's'} could not be saved.
        </Box>
      )}

      {!embedded && (
        <IntakePageFooter
          primaryLabel={nextLabel}
          onPrimary={onContinue}
          onBack={onBack}
          saveLabel={saveMode === 'explicit' ? (isSaving ? 'Saving…' : 'Save changes') : undefined}
          onSave={saveMode === 'explicit' ? () => void onSaveChanges?.() : undefined}
          saveDisabled={saveMode === 'explicit' && (isSaving || (saveDisabled && !availityDirty))}
          disabled={!availityValid}
          disabledReason={
            !availityValid ? 'Complete all required fields before continuing.' : undefined
          }
        />
      )}
      <HelpPanelDrawer
        drawerId={drawerId}
        panel={selectedGroup?.help_panel ?? null}
        open={Boolean(selectedGroup?.help_panel)}
        onClose={closeGroup}
        onAction={onHelpAction}
      />
    </>
  );
}
